import { ColorUtil, MathUtil } from 'app/modules/editor/scripts/common';
import { clamp } from 'lodash-es';
import { Property } from './Property';

export class ColorProperty extends Property<string> {
  // @Override
  protected setter(model: any, propertyName: string, value: string | undefined) {
    // Colors are stored as Android color strings, and '' or undefined means no color. Convert
    // anything else (e.g. an SVG color like 'red' from an older file) so that it can be drawn and
    // animated. Undefined stays undefined, since exporters leave out undefined values (such as a
    // cleared block value) but write out empty strings.
    const isValid = !value || (typeof value === 'string' && !!ColorUtil.parseAndroidColor(value));
    if (!isValid) {
      const color = parseColor(String(value));
      value = color ? ColorUtil.toAndroidString(color) : '';
    }
    super.setter(model, propertyName, value);
  }

  // @Override
  setEditableValue(model: any, propertyName: string, value: string) {
    const color = value ? parseColor(value) : undefined;
    model[propertyName] = color ? ColorUtil.toAndroidString(color) : undefined;
  }

  // @Override
  interpolateValue(start: string, end: string, f: number) {
    if (!start || !end) {
      return '';
    }
    const s = ColorUtil.parseAndroidColor(start);
    const e = ColorUtil.parseAndroidColor(end);
    if (!s || !e) {
      return start;
    }
    return ColorUtil.toAndroidString({
      r: clamp(Math.round(MathUtil.lerp(s.r, e.r, f)), 0, 0xff),
      g: clamp(Math.round(MathUtil.lerp(s.g, e.g, f)), 0, 0xff),
      b: clamp(Math.round(MathUtil.lerp(s.b, e.b, f)), 0, 0xff),
      a: clamp(Math.round(MathUtil.lerp(s.a, e.a, f)), 0, 0xff),
    });
  }

  // @Override
  getAnimatorValueType() {
    return 'colorType';
  }

  // @Override
  getTypeName() {
    return 'ColorProperty';
  }
}

/** Parses an Android color, or an SVG color (which may be a name like 'red'). */
function parseColor(value: string) {
  const color = ColorUtil.parseAndroidColor(value);
  if (color) {
    return color;
  }
  // This is undefined for 'none'.
  const androidColor = ColorUtil.svgToAndroidColor(value);
  return androidColor ? ColorUtil.parseAndroidColor(androidColor) : undefined;
}
